import { proxy, ref, useSnapshot } from "valtio";
import { derive, proxyMap, proxySet } from "valtio/utils";

type EventFormData = {
	day?: number;
	month?: number;
	title?: string;
};

type PopupUserDate = Required<eXpress.core.IUserDate>;
export type SpecialDay = Exclude<keyof eXpress.creator.calendars.ICalendarDataSources, "UserDate">;

function isPopupUserDate(userDate: eXpress.core.IUserDate): userDate is PopupUserDate {
	return !!userDate.id;
}

const baseState = proxy<{
	formValues: EventFormData;
	selectedUserDateId: eXpress.GuidString | undefined;
	userDates: Map<eXpress.GuidString, PopupUserDate>;
	newUserDateIds: Set<eXpress.GuidString>;
	deletedUserDateIds: Set<eXpress.GuidString>;
	updatedUserDateIds: Set<eXpress.GuidString>;
	selectedUserDateIds: Set<eXpress.GuidString>;
	specialDays: Map<SpecialDay, boolean>;
}>({
	formValues: {
		day: undefined,
		month: undefined,
		title: undefined,
	},
	selectedUserDateId: undefined,
	userDates: proxyMap([]),
	deletedUserDateIds: ref(new Set([])),
	updatedUserDateIds: ref(new Set([])),
	newUserDateIds: ref(new Set([])),
	selectedUserDateIds: proxySet([]),
	specialDays: proxyMap([]),
});

const eventsState = derive(
	{
		// the user date being edited by the user
		selectedUserDate: (get) => {
			const { selectedUserDateId, userDates } = get(baseState);
			return selectedUserDateId ? userDates.get(selectedUserDateId) : undefined;
		},
		// all user dates that have their checkbox checked in the dialog
		selectedUserDates: (get) => {
			const { selectedUserDateIds, userDates } = get(baseState);
			return (
				Array.from(selectedUserDateIds.keys())
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					.map((id) => userDates.get(id)!)
			);
		},
		sortedUserDates: (get) => {
			const { userDates } = get(baseState);
			return eXpress.creator.calendars.calendarUtils.sortUserDates(
				Array.from(userDates.values()),
				eXpress.creator.calendars.calendarUtils.sortUserDatesByName,
			);
		},
	},
	{
		proxy: baseState,
	},
);

const isExistingEvent = (val: eXpress.core.IUserDate) => {
	const existingDates = Array.from(eventsState.userDates.values());
	const foundDate = existingDates.some(
		(date) =>
			date.dateCode === val.dateCode && date.value.toLowerCase() === val.value.toLowerCase(),
	);

	return foundDate;
};

const actions = {
	addUserDate: (userDate: eXpress.core.IUserDate) => {
		if (isExistingEvent(userDate)) return undefined;
		const newGuid = eXpress.platform.guidString.create();
		baseState.newUserDateIds.add(newGuid);
		baseState.selectedUserDateIds.add(newGuid);

		baseState.userDates.set(newGuid, {
			...userDate,
			id: newGuid,
		});
		return newGuid;
	},
	updateUserDate(userDate: PopupUserDate) {
		baseState.userDates.set(userDate.id, userDate);
		baseState.updatedUserDateIds.add(userDate.id);
		baseState.selectedUserDateIds.add(userDate.id);
	},
	toggleUserDate(id: eXpress.GuidString) {
		if (!baseState.selectedUserDateIds.delete(id)) {
			baseState.selectedUserDateIds.add(id);
		}
	},
	toggleAll(on: boolean) {
		if (!on) {
			baseState.selectedUserDateIds.clear();
		} else {
			baseState.selectedUserDateIds = proxySet(baseState.userDates.keys());
		}
	},
	deleteUserDate: (id: eXpress.GuidString) => {
		if (!id) return;
		// remove user date from all dirty tracker sets
		baseState.newUserDateIds.delete(id);
		baseState.updatedUserDateIds.delete(id);
		baseState.selectedUserDateIds.delete(id);
		baseState.deletedUserDateIds.add(id);

		baseState.userDates.delete(id);
	},
	setSelectedUserDateId: (id: eXpress.GuidString | undefined) => {
		baseState.selectedUserDateId = id;
	},
	addUserDates: (userDates: eXpress.core.IUserDate[]) => {
		userDates.forEach((userDate) => {
			// only add user dates with a value
			if (!userDate.value) {
				return;
			}

			if (isPopupUserDate(userDate)) {
				// user dates from account (with id) are added as is
				baseState.userDates.set(userDate.id, userDate);
			} else {
				// user dates from the definition (without id) are merged with existing user dates from account
				const existingUserDates = Array.from(baseState.userDates.values());
				const existing = existingUserDates.find(
					(date) =>
						date.value.toLowerCase() === userDate.value.toLowerCase() &&
						date.dateCode === userDate.dateCode,
				);

				if (existing && isPopupUserDate(existing)) {
					baseState.selectedUserDateIds.add(existing.id);

					// if casing of event title is different, mark the user date for update
					if (existing.value !== userDate.value) {
						baseState.updatedUserDateIds.add(existing.id);
					}
					baseState.userDates.set(existing.id, {
						...existing,
						value: userDate.value,
					});
				} else {
					// user date from definition not found in user dates for account
					// add definition user date as is and add it to list of new user dates to save it on account
					const newGuid = eXpress.platform.guidString.create();
					baseState.userDates.set(newGuid, {
						...userDate,
						id: newGuid,
					});
				}
			}
		});
	},
	clear: () => {
		baseState.selectedUserDateIds.clear();
		baseState.updatedUserDateIds.clear();
		baseState.deletedUserDateIds.clear();
		baseState.newUserDateIds.clear();
		baseState.userDates.clear();
		baseState.selectedUserDateId = undefined;
	},
	clearForm: () => {
		baseState.selectedUserDateId = undefined;
		baseState.formValues = {
			day: undefined,
			month: undefined,
			title: undefined,
		};
	},
	updateSpecialDays: (
		data: {
			key: SpecialDay;
			show: boolean;
		}[],
	) => {
		data.forEach(({ show, key }) => {
			baseState.specialDays.set(key, show);
		});
	},
	setFormValues: (values: EventFormData) => {
		baseState.formValues = values;
	},
};

export const useUserDateDialogActions = () => actions;

export const useUserDateDialogState = () => {
	/**
	 * Don't use Generic type of useSnapshot, it's too strict
	 * https://github.com/pmndrs/valtio
	 * The snap variable returned by useSnapshot is a (deeply) read-only object. Its type has readonly attribute, which may be too strict for some use cases.
	 * To mitigate typing difficulties, you might want to loosen the type definition: function useSnapshot<T extends object>(p: T): T
	 */
	return useSnapshot(eventsState) as typeof baseState & typeof eventsState;
};
